import React, { useState, useEffect, useRef } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const ProblemSection = () => {
  const [activeAnimation, setActiveAnimation] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [barsAnimated, setBarsAnimated] = useState(false);

  const ctaRef = useRef(null);
  const ctaImgRef = useRef(null);
  const sectionRef = useRef(null);
  const weatherRef = useRef(null);

  const problems = [
    {
      id: 1,
      icon: 'CloudRain',
      title: 'Poškodenie počasím',
      description: 'Dážď, sneh a vietor poškodzujú nábytok a podlahu balkóna',
      statistic: '85%',
      statisticLabel: 'balkónov trpí poškodením počasím',
      image: "../../../assets/images/photos/crashed.webp",
      imageAlt: 'Damaged balcony furniture and flooring after heavy rain and wind exposure',
      color: 'text-blue-500',
    },
    {
      id: 2,
      icon: 'Volume2',
      title: 'Hluk z ulice',
      description: 'Rušivé zvuky z dopravy a okolia znižujú komfort bývania',
      statistic: '70dB+',
      statisticLabel: 'priemerná hladina hluku na balkónoch',
      image: "../../../assets/images/photos/sound.webp",
      imageAlt: 'Urban balcony exposed to heavy traffic noise and city sounds',
      color: 'text-red-500'
    },
    {
      id: 3,
      icon: 'Snowflake',
      title: 'Obmedzené využitie',
      description: 'Balkón sa dá využívať len 4-5 mesiacov v roku kvôli počasiu',
      statistic: '60%',
      statisticLabel: 'času je balkón nevyužiteľný',
      image: "../../../assets/images/photos/cold.webp",
      imageAlt: 'Empty unused balcony covered in snow during winter months',
      color: 'text-cyan-500'
    },
    {
      id: 4,
      icon: 'TrendingDown',
      title: 'Strata hodnoty',
      description: 'Nechránený balkón znižuje celkovú hodnotu nehnuteľnosti',
      statistic: '15%',
      statisticLabel: 'pokles hodnoty nehnuteľnosti',
      image: "../../../assets/images/photos/forsale.webp",
      imageAlt: 'Deteriorating unprotected balcony reducing property value and appeal',
      color: 'text-orange-500'
    }
  ];

  // Автопоказ + триггеры видимости
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const sObs = new IntersectionObserver(([e]) => e.isIntersecting && setIsVisible(true), { threshold: 0.2 });
    sObs.observe(el);
    return () => sObs.disconnect();
  }, []);

  useEffect(() => {
    const el = weatherRef.current;
    if (!el) return;
    const wObs = new IntersectionObserver(([e]) => e.isIntersecting && setBarsAnimated(true), { threshold: 0.25 });
    wObs.observe(el);
    return () => wObs.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const id = setInterval(() => {
      setActiveAnimation((p) => (p + 1) % problems.length);
    }, 3000);
    return () => clearInterval(id);
  }, [isVisible, problems.length]);

  // Параллакс CTA
  useEffect(() => {
    let raf = 0;
    const update = () => {
      const el = ctaRef.current;
      const img = ctaImgRef.current;
      if (!el || !img) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.max(-1, Math.min(1, (rect.top + rect.height / 3 - vh / 3) / vh));
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const translateY = p * (isMobile ? -60 : -66);
      const scale = (isMobile ? 1.04 : 1.06) + Math.abs(p) * (isMobile ? 0.06 : 0.06);
      img.style.transform = `translate3d(0, ${translateY}px, 0) scale(${scale})`;
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; update(); });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // helper для обрезки описания без плагина line-clamp
  const clampTwo = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  };

  return (
    <section
      id="problem-section"
      ref={sectionRef}
      className="py-8 sm:py-10 md:py-20 bg-gradient-to-b from-muted to-background touch-pan-y"
    >
      <div className="container mx-auto px-4">
        {/* Header — компактнее на мобилке */}
        <div className="text-center mb-6 sm:mb-8 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-heading font-bold text-primary mb-2 sm:mb-4">
            Prečo trpí váš balkón?
          </h2>
          <p className="text-sm sm:text-base md:text-xl text-text-secondary font-body max-w-2xl md:max-w-3xl mx-auto leading-relaxed">
            Nechránené balkóny čelia každodenným problémom, ktoré znižujú komfort bývania a hodnotu nehnuteľnosti.
          </p>
        </div>

        {/* Problems Grid — 2 колонки на мобилке для экономии места */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-8 mb-8 md:mb-16">
          {problems.map((problem, index) => (
            <div
              key={problem.id}
              className={[
                'relative bg-white rounded-xl md:rounded-2xl shadow-md md:shadow-lg border border-border overflow-hidden',
                'transition-all duration-500 hover:shadow-xl hover:-translate-y-1 md:hover:-translate-y-2',
                activeAnimation === index ? 'ring-2 ring-accent md:scale-105' : ''
              ].join(' ')}
            >
              {/* Image — ниже высота на мобилке */}
              <div className="relative h-28 sm:h-36 md:h-48 overflow-hidden bg-black">
                <Image
                  src={problem.image}
                  alt={problem.imageAlt}
                  className="w-full h-full object-cover object-[50%_30%] transition-transform duration-500 hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <div
                  className={[
                    'absolute top-2 right-2 sm:top-3 sm:right-3 w-9 h-9 sm:w-10 sm:h-10',
                    'bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center',
                    problem.color
                  ].join(' ')}
                >
                  <Icon name={problem.icon} size={18} className="sm:hidden" />
                  <Icon name={problem.icon} size={20} className="hidden sm:inline" />
                </div>
                {activeAnimation === index && (
                  <div className="absolute inset-0 border-2 md:border-4 border-accent animate-pulse pointer-events-none" />
                )}
              </div>

              {/* Content — компактнее паддинги/шрифты и clamp описания */}
              <div className="p-3.5 sm:p-4 md:p-6">
                <h3 className="text-sm sm:text-base md:text-xl font-heading font-semibold text-primary mb-1.5 sm:mb-2.5">
                  {problem.title}
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-text-secondary font-body mb-3 md:mb-4 leading-snug" style={clampTwo}>
                  {problem.description}
                </p>
                <div className="bg-muted rounded-lg p-2.5 sm:p-3.5 md:p-4">
                  <div className={['text-xl sm:text-2xl md:text-3xl font-heading font-bold mb-0.5 sm:mb-1', problem.color].join(' ')}>
                    {problem.statistic}
                  </div>
                  <div className="text-[11px] sm:text-xs md:text-sm text-text-secondary font-body">
                    {problem.statisticLabel}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Weather Impact — компактные размеры на мобилке, анимация линий сохранена */}
        <div
          ref={weatherRef}
          className="bg-white rounded-xl md:rounded-2xl shadow-lg md:shadow-xl border border-border p-4 sm:p-6 md:p-8 mb-8 md:mb-16"
        >
          <div className="text-center mb-4 sm:mb-6 md:mb-8">
            <h3 className="text-lg sm:text-xl md:text-2xl font-heading font-semibold text-primary mb-2 md:mb-4">
              Ročný dopad počasia na nechránený balkón
            </h3>
            <p className="text-xs sm:text-sm md:text-base text-text-secondary font-body">
              Sledujte, ako počasie postupne poškodzuje váš balkón počas roka
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3.5 sm:gap-5 md:gap-6">
            {[
              { season: 'Jar',   damage: '25%', icon: 'CloudRain',  color: 'bg-green-100 text-green-600',   description: 'Začiatok poškodenia dažďom' },
              { season: 'Leto',  damage: '45%', icon: 'Sun',        color: 'bg-yellow-100 text-yellow-600', description: 'UV žiarenie a horúčavy' },
              { season: 'Jeseň', damage: '70%', icon: 'Wind',       color: 'bg-orange-100 text-orange-600', description: 'Silné vetry a dážď' },
              { season: 'Zima',  damage: '90%', icon: 'Snowflake',  color: 'bg-blue-100 text-blue-600',     description: 'Mráz a sneh dokončujú škody' }
            ].map((season, index) => (
              <div key={index} className="text-center">
                <div className={['w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16', season.color, 'rounded-full flex items-center justify-center mx-auto mb-2.5 sm:mb-3.5 md:mb-4'].join(' ')}>
                  <Icon name={season.icon} size={22} className="sm:hidden" />
                  <Icon name={season.icon} size={28} className="hidden sm:inline md:hidden" />
                  <Icon name={season.icon} size={32} className="hidden md:inline" />
                </div>
                <h4 className="text-sm sm:text-base md:text-lg font-heading font-semibold text-primary mb-1 sm:mb-2">
                  {season.season}
                </h4>
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-destructive mb-1 sm:mb-2">
                  {season.damage}
                </div>
                <p className="text-[11px] sm:text-xs md:text-sm text-text-secondary font-body">
                  {season.description}
                </p>

                {/* Progress */}
                <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2 mt-2.5 sm:mt-3 overflow-hidden">
                  <div
                    className="bg-destructive h-full rounded-full transition-all duration-1000 ease-out"
                    style={{
                      width: barsAnimated ? season.damage : '0%',
                      transitionDelay: `${index * 140}ms`,
                      willChange: 'width'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA — компактнее на мобилке */}
        <div
          ref={ctaRef}
          className="relative rounded-xl md:rounded-2xl text-center text-white overflow-hidden shadow-lg md:shadow-xl border border-white/10 min-h-[200px] sm:min-h-[260px] md:min-h-0"
        >
          <div
            ref={ctaImgRef}
            className="absolute inset-0 bg-center bg-cover will-change-transform pointer-events-none"
            style={{
              backgroundImage: 'url(../../../assets/images/photos/main.webp)',
              transform: 'translate3d(0,0,0) scale(1.08)',
              transformOrigin: 'center'
            }}
          />
          <div className="absolute inset-0 bg-black/45 pointer-events-none" />

          <div className="relative p-5 sm:p-6 md:p-10">
            <Icon name="Shield" size={32} color="orange" className="mx-auto mb-2.5 sm:mb-3.5" />
            <h3 className="text-lg sm:text-xl md:text-2xl font-heading font-bold text-white/90 mb-2.5 md:mb-4">
              Chráňte svoj balkón pred týmito problémami
            </h3>
            <p className="text-white/90 font-body mb-4 sm:mb-5 md:mb-6 max-w-2xl mx-auto text-sm sm:text-base">
              Naše zasklenie poskytuje kompletnú ochranu pred všetkými týmito problémami.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  const element = document.querySelector('#solution-section');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-[#E67E22] px-5 py-2 sm:px-6 sm:py-2.5 md:px-8 md:py-3 rounded-lg text-white font-semibold hover:bg-black/90 transition-smooth inline-flex items-center gap-2"
              >
                <Icon name="ArrowRight" size={18} className="sm:hidden" />
                <Icon name="ArrowRight" size={20} className="hidden sm:inline" />
                <span>Pozrieť riešenie</span>
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ProblemSection;